/**
 * TheGraphMonitor Page
 *
 * Monitor The Graph (GRT) delegations, rewards, and network statistics.
 * Track delegator positions across multiple indexers with real-time APY.
 * Similar design to HederaMonitor and FluxNodeMonitor.
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useTheGraphMonitorWallets } from '../hooks/useMonitorWallets';
import { useTheGraphAggregated } from '../hooks/useTheGraphAggregated';
import { thegraphAPI, isValidEthereumAddress, isApiKeyConfigured } from '../services/thegraphApi';
import { useToast } from '../components/Toast';
import { useQuery } from '@tanstack/react-query';
import type {
  TheGraphWalletData,
  TheGraphDelegation,
  GraphNetworkStats,
  GraphIndexer,
} from '../types/thegraph.types';
import { THEGRAPH_COLORS, THEGRAPH_CONSTANTS } from '../types/thegraph.types';
import { TheGraphHealthMetrics } from '../components/thegraph/TheGraphHealthMetrics';
import { GrtSupplyBreakdown } from '../components/thegraph/GrtSupplyBreakdown';
import {
  NetworkMonitorPageHeader,
  NetworkAddressInputForm,
  NetworkMonitorEmptyState,
  MonitorTabs,
} from '../components/monitor';

// ============================================
// STYLES
// ============================================

const s = {
  page: {
    padding: '0',
    maxWidth: '1400px',
    margin: '0 auto',
    paddingBottom: '40px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    gap: '20px',
    flexWrap: 'wrap' as const,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: THEGRAPH_COLORS.gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleText: {
    fontSize: '24px',
    fontWeight: 700,
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: 'var(--muted)',
    marginTop: '4px',
  },
  card: {
    background: 'rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '20px',
    marginBottom: '20px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: (color: string) => ({
    background: `${color}15`,
    border: `1px solid ${color}40`,
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center' as const,
  }),
  statValue: {
    fontSize: '22px',
    fontWeight: 700,
  },
  statLabel: {
    fontSize: '11px',
    color: 'var(--muted)',
    marginTop: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '10px',
    color: 'var(--text)',
    fontSize: '13px',
    fontFamily: 'monospace',
    outline: 'none',
  },
  button: {
    padding: '12px 20px',
    background: THEGRAPH_COLORS.gradient,
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  buttonSecondary: {
    padding: '12px 20px',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'var(--text)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  tabContainer: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '14px',
    width: 'fit-content',
    marginBottom: '24px',
  },
  tab: (isActive: boolean) => ({
    padding: '10px 18px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    background: isActive ? THEGRAPH_COLORS.gradient : 'transparent',
    color: isActive ? 'white' : 'var(--muted)',
    transform: isActive ? 'scale(1.02)' : 'scale(1)',
    boxShadow: isActive ? '0 4px 15px rgba(103, 71, 237, 0.3)' : 'none',
  }),
  emptyState: {
    textAlign: 'center' as const,
    padding: '60px 20px',
  },
  apiKeyWarning: {
    background: 'rgba(255, 193, 7, 0.15)',
    border: '1px solid rgba(255, 193, 7, 0.4)',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  delegationCard: {
    background: 'rgba(103, 71, 237, 0.08)',
    border: '1px solid rgba(103, 71, 237, 0.2)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
  },
  indexerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
  },
};

type TabType = 'overview' | 'delegations' | 'indexers' | 'network';

// ============================================
// SUB-COMPONENTS
// ============================================

/**
 * Network Stats Banner
 */
function NetworkStatsBanner({
  networkStats,
  grtPrice,
  networkApy,
  isLoading,
}: {
  networkStats: GraphNetworkStats | null;
  grtPrice: number;
  networkApy: number;
  isLoading: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        ...s.card,
        background: THEGRAPH_COLORS.lightGradient,
        border: '1px solid rgba(103, 71, 237, 0.25)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('thegraph.network.title', 'The Graph Network')}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: THEGRAPH_COLORS.primary }}>
              ${grtPrice.toFixed(4)}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>GRT</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600, color: THEGRAPH_COLORS.success }}>
              {networkApy.toFixed(2)}%
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('thegraph.network.apy', 'Avg APY')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>
              {networkStats?.activeIndexerCount || '-'}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('thegraph.network.indexers', 'Indexers')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>
              {networkStats?.activeDelegatorCount?.toLocaleString() || '-'}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('thegraph.network.delegators', 'Delegators')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: isLoading ? THEGRAPH_COLORS.warning : THEGRAPH_COLORS.success,
                boxShadow: `0 0 10px ${isLoading ? THEGRAPH_COLORS.warning : THEGRAPH_COLORS.success}`,
                margin: '0 auto 4px',
              }}
            />
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {isLoading
                ? t('thegraph.network.syncing', 'Syncing...')
                : t('thegraph.network.connected', 'Connected')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Stats Overview Cards
 */
function StatsOverview({
  totalDelegated,
  totalDelegatedUsd,
  pendingRewards,
  pendingRewardsUsd,
  realizedRewards,
  realizedRewardsUsd,
  delegationsCount,
  avgApy,
}: {
  totalDelegated: number;
  totalDelegatedUsd: number;
  pendingRewards: number;
  pendingRewardsUsd: number;
  realizedRewards: number;
  realizedRewardsUsd: number;
  delegationsCount: number;
  avgApy: number;
}) {
  const { t } = useTranslation();

  const stats = [
    {
      label: t('thegraph.stats.delegated', 'Total Delegated'),
      value: `${totalDelegated.toLocaleString(undefined, { maximumFractionDigits: 0 })} GRT`,
      subvalue: `$${totalDelegatedUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: THEGRAPH_COLORS.primary,
      icon: <Icons.TheGraph size={16} color={THEGRAPH_COLORS.primary} />,
    },
    {
      label: t('thegraph.stats.pending', 'Pending Rewards'),
      value: `${pendingRewards.toLocaleString(undefined, { maximumFractionDigits: 2 })} GRT`,
      subvalue: `$${pendingRewardsUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      color: THEGRAPH_COLORS.warning,
      icon: <Icons.Clock size={16} />,
    },
    {
      label: t('thegraph.stats.realized', 'Realized Rewards'),
      value: `${realizedRewards.toLocaleString(undefined, { maximumFractionDigits: 2 })} GRT`,
      subvalue: `$${realizedRewardsUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      note: t('thegraph.stats.realizedNote', 'Auto-reinvested'),
      color: THEGRAPH_COLORS.success,
      icon: <Icons.TrendingUp size={16} />,
    },
    {
      label: t('thegraph.stats.delegations', 'Delegations'),
      value: delegationsCount.toString(),
      subvalue: `~${avgApy.toFixed(1)}% APY`,
      color: THEGRAPH_COLORS.accent,
      icon: <Icons.Users size={16} />,
    },
  ];

  return (
    <div style={s.statsGrid}>
      {stats.map((stat, idx) => (
        <div key={idx} style={s.statCard(stat.color)}>
          <div style={{ color: stat.color, marginBottom: '8px' }}>{stat.icon}</div>
          <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
            {stat.subvalue}
          </div>
          <div style={s.statLabel}>{stat.label}</div>
          {stat.note && (
            <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '4px', fontStyle: 'italic' }}>
              {stat.note}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Wallet Card Component
 */
function WalletCard({
  wallet,
  onRemove,
}: {
  wallet: TheGraphWalletData;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  const avgApy =
    wallet.delegations.length > 0
      ? wallet.delegations.reduce((sum, d) => sum + d.estimatedApy, 0) / wallet.delegations.length
      : 0;

  return (
    <div style={s.delegationCard}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: THEGRAPH_COLORS.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.Wallet size={20} color="white" />
          </div>
          <div>
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: '2px',
              }}
            >
              {thegraphAPI.formatAddress(wallet.address)}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span
                style={{
                  ...s.indexerBadge,
                  background: 'rgba(16, 185, 129, 0.15)',
                  color: THEGRAPH_COLORS.success,
                }}
              >
                <Icons.TheGraph size={10} color={THEGRAPH_COLORS.primary} />
                {wallet.delegationsCount} {t('thegraph.delegations', 'Delegations')}
              </span>
              {avgApy > 0 && (
                <span
                  style={{
                    ...s.indexerBadge,
                    background: 'rgba(103, 71, 237, 0.15)',
                    color: THEGRAPH_COLORS.primary,
                  }}
                >
                  ~{avgApy.toFixed(1)}% APY
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <a
            href={thegraphAPI.getExplorerUrl(wallet.address)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'rgba(103, 71, 237, 0.15)',
              color: THEGRAPH_COLORS.primary,
              border: 'none',
              borderRadius: '6px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              textDecoration: 'none',
            }}
          >
            <Icons.ExternalLink size={12} />
          </a>
          <button
            onClick={onRemove}
            style={{
              background: 'rgba(239, 68, 68, 0.15)',
              color: '#ef4444',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
            }}
          >
            <Icons.Trash2 size={12} />
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('thegraph.wallet.delegated', 'Delegated')}
          </div>
          <div style={{ fontSize: '18px', fontWeight: 600 }}>
            {wallet.totalDelegated.toLocaleString(undefined, { maximumFractionDigits: 0 })} GRT
          </div>
          <div style={{ fontSize: '11px', color: THEGRAPH_COLORS.success }}>
            ${wallet.totalDelegatedUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('thegraph.wallet.pending', 'Pending')}
          </div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: THEGRAPH_COLORS.warning }}>
            {wallet.pendingRewards.toLocaleString(undefined, { maximumFractionDigits: 2 })} GRT
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('thegraph.wallet.realized', 'Realized')}
            <span style={{ fontSize: '9px', fontStyle: 'italic', marginLeft: '4px' }}>
              ({t('thegraph.wallet.autoReinvested', 'auto-reinvested')})
            </span>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: THEGRAPH_COLORS.success }}>
            {wallet.totalRealizedRewards.toLocaleString(undefined, { maximumFractionDigits: 2 })} GRT
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('thegraph.wallet.unstaking', 'Unstaking')}
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {wallet.totalUnstaking > 0
              ? `${wallet.totalUnstaking.toLocaleString(undefined, { maximumFractionDigits: 0 })} GRT`
              : '-'}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Delegation Row Component
 */
function DelegationRow({ delegation }: { delegation: TheGraphDelegation }) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        ...s.delegationCard,
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
        alignItems: 'center',
        gap: '16px',
      }}
    >
      {/* Indexer Info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: THEGRAPH_COLORS.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icons.Server size={18} color="white" />
        </div>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
            {delegation.indexer.name || thegraphAPI.formatAddress(delegation.indexer.id)}
            <a
              href={`https://thegraph.com/explorer/profile/${delegation.indexer.id}?view=indexing`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: THEGRAPH_COLORS.primary,
                display: 'flex',
                alignItems: 'center',
              }}
              title="Open on The Graph Explorer"
            >
              <Icons.ExternalLink size={14} />
            </a>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', fontFamily: 'monospace' }}>
            {thegraphAPI.formatAddress(delegation.indexer.id)}
          </div>
        </div>
      </div>

      {/* Staked Amount */}
      <div>
        <div style={{ fontSize: '14px', fontWeight: 600 }}>
          {delegation.stakedTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })} GRT
        </div>
        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
          ${delegation.stakedTokensUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </div>
      </div>

      {/* Rewards */}
      <div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: THEGRAPH_COLORS.success }}>
          {delegation.realizedRewards.toLocaleString(undefined, { maximumFractionDigits: 2 })} GRT
        </div>
        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
          {t('thegraph.delegation.realized', 'Realized')}
          <span style={{ fontSize: '9px', fontStyle: 'italic', display: 'block' }}>
            ({t('thegraph.delegation.autoReinvested', 'auto-reinvested')})
          </span>
        </div>
      </div>

      {/* APY */}
      <div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: THEGRAPH_COLORS.primary }}>
          {delegation.estimatedApy.toFixed(2)}%
        </div>
        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
          {t('thegraph.delegation.apy', 'Est. APY')}
        </div>
      </div>

      {/* Indexer Cuts */}
      <div>
        <div style={{ fontSize: '12px' }}>
          <span style={{ color: 'var(--muted)' }}>Cut: </span>
          <span style={{ fontWeight: 600 }}>{delegation.indexer.indexingRewardCut.toFixed(1)}%</span>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
          Query fee cut: {delegation.indexer.queryFeeCut.toFixed(1)}%
        </div>
      </div>
    </div>
  );
}

/**
 * Indexer Card Component
 */
function IndexerCard({ indexer, grtPrice }: { indexer: GraphIndexer; grtPrice: number }) {
  const { t } = useTranslation();

  const fromWei = (value: string) => {
    try {
      return Number(BigInt(value) / BigInt(10 ** 18));
    } catch {
      return 0;
    }
  };

  const stakedTokens = fromWei(indexer.stakedTokens);
  const delegatedTokens = fromWei(indexer.delegatedTokens);
  const indexingCut = indexer.indexingRewardCut / 10000;

  return (
    <div style={s.delegationCard}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: THEGRAPH_COLORS.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.Server size={18} color="white" />
          </div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>
              {indexer.defaultDisplayName || thegraphAPI.formatAddress(indexer.id)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', fontFamily: 'monospace' }}>
              {thegraphAPI.formatAddress(indexer.id)}
            </div>
          </div>
        </div>
        <a
          href={thegraphAPI.getExplorerUrl(indexer.id, 'indexer')}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            background: 'rgba(103, 71, 237, 0.15)',
            color: THEGRAPH_COLORS.primary,
            border: 'none',
            borderRadius: '6px',
            padding: '6px 10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
            textDecoration: 'none',
          }}
        >
          <Icons.ExternalLink size={12} />
          Explorer
        </a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('thegraph.indexer.selfStake', 'Self Stake')}
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {stakedTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })} GRT
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('thegraph.indexer.delegated', 'Delegated')}
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: THEGRAPH_COLORS.primary }}>
            {delegatedTokens.toLocaleString(undefined, { maximumFractionDigits: 0 })} GRT
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('thegraph.indexer.cut', 'Reward Cut')}
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{indexingCut.toFixed(1)}%</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('thegraph.indexer.queryFeeCut', 'Query Fee Cut')}
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{(indexer.queryFeeCut / 10000).toFixed(1)}%</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Empty State Component
 */
function EmptyState() {
  const { t } = useTranslation();

  const features = [
    {
      icon: <Icons.TheGraph size={28} color={THEGRAPH_COLORS.primary} />,
      title: t('thegraph.empty.delegations', 'Delegations'),
      desc: t('thegraph.empty.delegationsDesc', 'Track GRT delegated'),
    },
    {
      icon: <Icons.TrendingUp size={28} />,
      title: t('thegraph.empty.rewards', 'Rewards'),
      desc: t('thegraph.empty.rewardsDesc', 'Monitor earnings'),
    },
    {
      icon: <Icons.TheGraph size={28} />,
      title: t('thegraph.empty.indexers', 'Indexers'),
      desc: t('thegraph.empty.indexersDesc', 'View top indexers'),
    },
    {
      icon: <Icons.Activity size={28} />,
      title: t('thegraph.empty.network', 'Network'),
      desc: t('thegraph.empty.networkDesc', 'Global statistics'),
    },
  ];

  return (
    <NetworkMonitorEmptyState
      icon={<Icons.TheGraph size={40} color="white" />}
      title={t('thegraph.empty.title', 'Welcome to The Graph Monitor')}
      description={t(
        'thegraph.empty.description',
        'Track your GRT delegations, monitor rewards, and analyze indexer performance - all without connecting your wallet.',
      )}
      colors={THEGRAPH_COLORS}
    >
      {/* TheGraph-specific feature grid below the description. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '16px',
          maxWidth: '600px',
          margin: '0 auto',
        }}
      >
        {features.map((feature, idx) => (
          <div
            key={idx}
            style={{
              padding: '20px 16px',
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <div style={{ color: THEGRAPH_COLORS.primary, marginBottom: '12px' }}>
              {feature.icon}
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
              {feature.title}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{feature.desc}</div>
          </div>
        ))}
      </div>
    </NetworkMonitorEmptyState>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function TheGraphMonitor() {
  const { t } = useTranslation();
  const toast = useToast();

  // Wallet management with Supabase sync
  const { wallets, setWallets, isSyncing } = useTheGraphMonitorWallets();

  const [newWalletInput, setNewWalletInput] = useState('');
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Fetch GRT price
  const { data: grtPrice = 0 } = useQuery({
    queryKey: ['grt-price'],
    queryFn: () => thegraphAPI.getGrtPrice(),
    staleTime: 60000, // 1 minute
  });

  // Fetch network stats
  const { data: networkStats } = useQuery({
    queryKey: ['graph-network-stats'],
    queryFn: () => thegraphAPI.getNetworkStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch network APY
  const { data: networkApy = 0 } = useQuery({
    queryKey: ['graph-network-apy'],
    queryFn: () => thegraphAPI.calculateNetworkApy(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch top indexers
  const { data: topIndexers = [] } = useQuery({
    queryKey: ['graph-top-indexers'],
    queryFn: () => thegraphAPI.getTopIndexers(20),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch + aggregate wallet data via the shared hook (extracted from
  // this file's prior inline block so /networks/compare can re-use the
  // same totals — see useTheGraphAggregated.ts).
  const aggregated = useTheGraphAggregated(wallets);
  const {
    walletsData,
    isLoading,
    error: walletsError,
    refetch,
  } = aggregated;

  // Compose the page-level stats : 7 raw fields from the hook + the
  // weighted-APY calculation (which needs `networkApy` from another
  // query, kept here so the hook stays free of cross-query
  // dependencies).
  const aggregatedStats = useMemo(() => {
    const allDelegations = walletsData.flatMap((w) => w.delegations);
    const avgApy =
      allDelegations.length > 0
        ? allDelegations.reduce((sum, d) => sum + d.estimatedApy, 0) / allDelegations.length
        : networkApy;

    return {
      totalDelegated: aggregated.totalDelegated,
      totalDelegatedUsd: aggregated.totalDelegatedUsd,
      pendingRewards: aggregated.pendingRewards,
      pendingRewardsUsd: aggregated.pendingRewardsUsd,
      realizedRewards: aggregated.realizedRewards,
      realizedRewardsUsd: aggregated.realizedRewardsUsd,
      delegationsCount: aggregated.delegationsCount,
      avgApy,
    };
  }, [aggregated, walletsData, networkApy]);

  // All delegations
  const allDelegations = useMemo(() => {
    return walletsData.flatMap((w) =>
      w.delegations.map((d) => ({ ...d, walletAddress: w.address }))
    );
  }, [walletsData]);

  // Handle adding wallet
  const handleAddWallet = () => {
    const trimmedAddress = newWalletInput.trim().toLowerCase();

    if (!trimmedAddress) {
      toast(t('thegraph.toast.enterAddress', 'Please enter an Ethereum address'), 'warning', 3000);
      return;
    }

    if (!isValidEthereumAddress(trimmedAddress)) {
      toast(
        t('thegraph.toast.invalidFormat', 'Invalid Ethereum address format'),
        'error',
        4000
      );
      return;
    }

    if (wallets.includes(trimmedAddress)) {
      toast(t('thegraph.toast.alreadyAdded', 'This address is already added'), 'warning', 3000);
      return;
    }

    setWallets([...wallets, trimmedAddress]);
    setNewWalletInput('');
    setShowAddWallet(false);
    toast(t('thegraph.toast.walletAdded', 'Wallet added successfully'), 'success', 3000);
  };

  // Handle removing wallet
  const handleRemoveWallet = (address: string) => {
    if (confirm(t('thegraph.confirm.removeWallet', `Remove wallet ${thegraphAPI.formatAddress(address)}?`))) {
      setWallets(wallets.filter((w) => w !== address));
      toast(t('thegraph.toast.walletRemoved', 'Wallet removed'), 'info', 3000);
    }
  };

  // Tabs configuration
  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: t('thegraph.tabs.overview', 'Overview'), icon: <Icons.BarChart size={16} /> },
    { id: 'delegations', label: t('thegraph.tabs.delegations', 'Delegations'), icon: <Icons.TheGraph size={16} color={THEGRAPH_COLORS.primary} /> },
    { id: 'indexers', label: t('thegraph.tabs.indexers', 'Indexers'), icon: <Icons.TheGraph size={16} /> },
    { id: 'network', label: t('thegraph.tabs.network', 'Network'), icon: <Icons.Activity size={16} /> },
  ];

  const isValidInput = newWalletInput.trim() && isValidEthereumAddress(newWalletInput.trim());

  return (
    <div style={s.page}>
      <NetworkMonitorPageHeader
        icon={
          <div style={s.titleIcon}>
            <Icons.TheGraph size={20} color="white" />
          </div>
        }
        title="The Graph Monitor"
        subtitle={t(
          'thegraph.subtitle',
          'Monitor your GRT delegations, rewards, and indexer performance',
        )}
        primaryAction={
          showAddWallet
            ? {
                label: t('common.cancel', 'Cancel'),
                icon: <Icons.X size={16} />,
                onClick: () => setShowAddWallet(false),
                variant: 'secondary',
              }
            : {
                label: t('thegraph.addWallet', 'Add Wallet'),
                icon: <Icons.Plus size={16} />,
                onClick: () => setShowAddWallet(true),
              }
        }
        refreshAction={
          wallets.length > 0
            ? {
                label: t('common.refresh', 'Refresh'),
                icon: <Icons.RefreshCw size={16} />,
                onClick: () => refetch(),
              }
            : undefined
        }
        colors={THEGRAPH_COLORS}
      />

      {/* API Key Warning */}
      {!isApiKeyConfigured() && (
        <div style={s.apiKeyWarning}>
          <Icons.AlertTriangle size={20} color="#FFC107" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#FFC107' }}>
              {t('thegraph.apiKeyRequired', 'API Key Required')}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.5 }}>
              {t('thegraph.apiKeyMessage', 'The Graph requires an API key to fetch delegation data. Get your free API key (100k queries/month) from')}
              {' '}
              <a
                href="https://thegraph.com/studio/apikeys/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: THEGRAPH_COLORS.primary, textDecoration: 'underline' }}
              >
                thegraph.com/studio/apikeys
              </a>
              {' '}
              {t('thegraph.apiKeyMessageEnd', 'and add it as VITE_THEGRAPH_API_KEY in your environment variables.')}
            </div>
          </div>
        </div>
      )}

      {/* V2.K.4 — GRT supply breakdown (mint indexer rewards vs burn
          fees + Horizon upgrade context). Fed by CoinGecko, refresh
          every 5 min. */}
      <GrtSupplyBreakdown />

      {/* Blockchain Health Metrics - Collapsible */}
      <TheGraphHealthMetrics />

      {/* API Error Warning */}
      {walletsError && (
        <div style={{
          ...s.apiKeyWarning,
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.4)',
        }}>
          <Icons.AlertTriangle size={20} color="#EF4444" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#EF4444' }}>
              {t('thegraph.apiError', 'API Error')}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.5 }}>
              {walletsError instanceof Error ? walletsError.message : t('thegraph.apiErrorMessage', 'Failed to fetch wallet data. Please check your API key and try again.')}
            </div>
          </div>
        </div>
      )}

      {showAddWallet && (
        <NetworkAddressInputForm
          value={newWalletInput}
          onChange={setNewWalletInput}
          onSubmit={handleAddWallet}
          submitLabel={t('common.add', 'Add')}
          placeholder={t('thegraph.placeholder', 'Enter Ethereum address (0x...)')}
          isValid={newWalletInput.trim() ? Boolean(isValidInput) : null}
          helperText={t(
            'thegraph.hint',
            'Enter the Ethereum address used for GRT delegation on Arbitrum',
          )}
          colors={THEGRAPH_COLORS}
        />
      )}

      {/* Network Stats Banner */}
      <NetworkStatsBanner
        networkStats={networkStats}
        grtPrice={grtPrice}
        networkApy={networkApy}
        isLoading={isLoading || isSyncing}
      />

      {/* Main Content */}
      {wallets.length > 0 ? (
        <>
          {/* Stats Overview */}
          <StatsOverview {...aggregatedStats} />

          {/* Tabs */}
          <MonitorTabs<TabType>
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            colors={THEGRAPH_COLORS}
            ariaLabel="The Graph monitor tabs"
          />

          {/* Tab Content: Overview */}
          {activeTab === 'overview' && (
            <div>
              <h2
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Icons.Wallet size={18} />
                {t('thegraph.wallets', 'Wallets')} ({wallets.length})
              </h2>
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Icons.Activity
                    size={32}
                    style={{ opacity: 0.3, animation: 'spin 2s linear infinite' }}
                  />
                  <p style={{ color: 'var(--muted)', marginTop: '12px' }}>
                    {t('thegraph.loading', 'Loading...')}
                  </p>
                </div>
              ) : (
                walletsData.map((wallet) => (
                  <WalletCard
                    key={wallet.address}
                    wallet={wallet}
                    onRemove={() => handleRemoveWallet(wallet.address)}
                  />
                ))
              )}
            </div>
          )}

          {/* Tab Content: Delegations */}
          {activeTab === 'delegations' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('thegraph.delegations.title', 'All Delegations')} ({allDelegations.length})
              </h3>
              {allDelegations.length > 0 ? (
                allDelegations.map((delegation, idx) => (
                  <DelegationRow key={`${delegation.id}-${idx}`} delegation={delegation} />
                ))
              ) : (
                <div style={{ ...s.card, textAlign: 'center', color: 'var(--muted)' }}>
                  {t('thegraph.delegations.empty', 'No delegations found')}
                </div>
              )}
            </div>
          )}

          {/* Tab Content: Indexers */}
          {activeTab === 'indexers' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('thegraph.indexers.title', 'Top Indexers')}
              </h3>
              {topIndexers.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '12px' }}>
                  {topIndexers.map((indexer) => (
                    <IndexerCard key={indexer.id} indexer={indexer} grtPrice={grtPrice} />
                  ))}
                </div>
              ) : (
                <div style={{ ...s.card, textAlign: 'center', color: 'var(--muted)' }}>
                  {t('thegraph.indexers.loading', 'Loading indexers...')}
                </div>
              )}
            </div>
          )}

          {/* Tab Content: Network */}
          {activeTab === 'network' && networkStats && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                {t('thegraph.network.statsTitle', 'Network Statistics')}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div style={s.statCard(THEGRAPH_COLORS.primary)}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                    {t('thegraph.network.totalStaked', 'Total GRT Staked')}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: THEGRAPH_COLORS.primary }}>
                    {(Number(BigInt(networkStats.totalTokensStaked) / BigInt(10 ** 18)) / 1e9).toFixed(2)}B
                  </div>
                </div>
                <div style={s.statCard(THEGRAPH_COLORS.accent)}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                    {t('thegraph.network.totalDelegated', 'Total Delegated')}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: THEGRAPH_COLORS.accent }}>
                    {(Number(BigInt(networkStats.totalDelegatedTokens) / BigInt(10 ** 18)) / 1e9).toFixed(2)}B
                  </div>
                </div>
                <div style={s.statCard(THEGRAPH_COLORS.success)}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                    {t('thegraph.network.indexingRewards', 'Total Indexing Rewards')}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: THEGRAPH_COLORS.success }}>
                    {(Number(BigInt(networkStats.totalIndexingRewards) / BigInt(10 ** 18)) / 1e6).toFixed(2)}M
                  </div>
                </div>
                <div style={s.statCard(THEGRAPH_COLORS.warning)}>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                    {t('thegraph.network.currentEpoch', 'Current Epoch')}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: THEGRAPH_COLORS.warning }}>
                    {networkStats.currentEpoch}
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div
                style={{
                  marginTop: '24px',
                  padding: '16px',
                  background: 'rgba(103, 71, 237, 0.08)',
                  borderRadius: '12px',
                  border: '1px solid rgba(103, 71, 237, 0.15)',
                }}
              >
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>
                  {t('thegraph.network.parameters', 'Protocol Parameters')}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>Delegation Tax:</span>
                    <span style={{ fontSize: '14px', fontWeight: 600, marginLeft: '8px' }}>
                      {THEGRAPH_CONSTANTS.DELEGATION_TAX_PERCENTAGE}%
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>Thawing Period:</span>
                    <span style={{ fontSize: '14px', fontWeight: 600, marginLeft: '8px' }}>
                      ~{THEGRAPH_CONSTANTS.THAWING_PERIOD_DAYS} days
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>Delegation Ratio:</span>
                    <span style={{ fontSize: '14px', fontWeight: 600, marginLeft: '8px' }}>
                      {THEGRAPH_CONSTANTS.DELEGATION_RATIO}x
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>Min Indexer Stake:</span>
                    <span style={{ fontSize: '14px', fontWeight: 600, marginLeft: '8px' }}>
                      {(THEGRAPH_CONSTANTS.MINIMUM_INDEXER_STAKE / 1000).toFixed(0)}K GRT
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <EmptyState />
      )}

      {/* Info Footer */}
      <div
        style={{
          marginTop: '30px',
          padding: '16px 20px',
          background: 'rgba(103, 71, 237, 0.08)',
          borderRadius: '12px',
          border: '1px solid rgba(103, 71, 237, 0.15)',
          fontSize: '12px',
          color: 'var(--muted)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Icons.Info size={14} style={{ color: THEGRAPH_COLORS.primary }} />
          <strong style={{ color: 'var(--text)' }}>
            {t('thegraph.info.title', 'About The Graph Monitor')}
          </strong>
        </div>
        <p style={{ lineHeight: 1.6 }}>
          {t(
            'thegraph.info.description',
            'This page uses The Graph Network Subgraph on Arbitrum to fetch delegation data. No wallet connection required - just enter your Ethereum address to start monitoring. Data refreshes every 2 minutes.'
          )}
        </p>
      </div>
    </div>
  );
}
